"use client";

// Ported from relay-protocol packages/relay-widget/src/icons.tsx (v0.1.0).
// Mechanical substitution only: the sole non-relative import
// (`./token-logos`) is retargeted to this package's `../lib/token-logos`
// (icons.tsx lives in components/, token-logos.ts lives in lib/) --
// everything else, including every inline SVG path/brand color and the
// licensing/sourcing notes below, is byte-identical to the source.
//
// Bundled icon registry for the widget's chain/token dropdowns. Everything
// here is a self-contained inline SVG (or an <img> pointed at a caller-
// supplied `logoUrl`) -- no external CDN fetch, no icon library dependency,
// no network I/O. Keeping icons inline (rather than shipping image assets)
// means the whole registry travels with `dist/index.js`, so a host consuming
// the widget from `file:`/npm gets working icons with zero extra setup.
//
// Official-logo path data (the `d` attributes + brand colors below) for the
// CHAIN marks -- Ethereum, BNB Smart Chain, Arbitrum, Polygon, Solana --
// was sourced from @web3icons/react (MIT license, https://github.com/0xa3k5/web3icons),
// a maintained icon set that vectorizes each network's official brand
// assets. (The USDT/USDC TOKEN logos are NOT here: they ship as canonical
// raster PNGs in token-logos.ts -- see that file's header -- rendered by
// TokenIcon, since the simplified web3icons vectorizations didn't match the
// coins' real brand marks.) We deliberately did NOT add web3icons as a
// runtime dependency: the
// package ships ~8.7k files / ~99MB unpacked (every chain/token variant),
// which is neither tree-shakeable at *install* time nor a reasonable
// addition to `dependencies` for the ~8 marks this widget needs -- so we
// vetted the source, then inlined only the specific path data for the
// chains/tokens we render. Base is the one exception: @web3icons/react
// vectorizes Base as a rounded-square app-icon glyph rather than the brand's
// circular logomark, so its path data is instead sourced directly from the
// official base-org/brand-kit repo (github.com/base-org/brand-kit,
// logo/symbol/Base_Symbol_Blue.svg, MIT license) -- see `BaseIcon` below for
// details. See `.superpowers/sdd/official-icons-report.md` and
// `.superpowers/sdd/base-icon-report.md` for the full source/license
// writeup.
import { useId, type ReactNode } from "react";
import { bundledTokenLogo } from "../lib/token-logos";

/** Uppercase-first monogram (1-2 chars) used as a graceful fallback when we
 * don't have a bundled brand icon for a given chain/token. */
function monogram(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return "?";
  return trimmed.slice(0, 2).toUpperCase();
}

interface BadgeProps {
  size: number;
  bg: string;
  fg?: string;
  children: ReactNode;
}

/** Shared circular-badge shell: a colored circle clipped to `size`, with
 * `children` (glyph/monogram markup) centered inside. */
function Badge({ size, bg, fg = "#ffffff", children }: BadgeProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flex: "none", borderRadius: "50%" }}
    >
      <circle cx="12" cy="12" r="12" fill={bg} />
      <g fill={fg} stroke={fg}>
        {children}
      </g>
    </svg>
  );
}

function MonogramBadge({
  size,
  bg,
  fg = "#ffffff",
  text,
}: {
  size: number;
  bg: string;
  fg?: string;
  text: string;
}) {
  return (
    <Badge size={size} bg={bg} fg={fg}>
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="10"
        fontWeight={700}
        fontFamily="inherit"
        stroke="none"
        fill={fg}
      >
        {text}
      </text>
    </Badge>
  );
}

/** Shared shell for the official brand SVGs below: a square canvas
 * (matching each source mark's native coordinate space -- 24x24 by default,
 * overridable via `viewBox` for marks authored in a different coordinate
 * space) that gets clipped to a circle by the same CSS technique `Badge`
 * uses -- the artwork fills the full square, and `borderRadius: 50%` on the
 * rendered element clips the corners, leaving the mark's own inscribed-
 * circle silhouette. */
function OfficialIcon({
  size,
  viewBox = "0 0 24 24",
  children,
}: {
  size: number;
  viewBox?: string;
  children: ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flex: "none", borderRadius: "50%" }}
    >
      {children}
    </svg>
  );
}

function EthereumIcon({ size }: { size: number }) {
  const clip = useId();
  return (
    <OfficialIcon size={size}>
      <g clipPath={`url(#${clip})`}>
        <path fill="#000" d="M24 0H0v24h24z" />
        <path fill="#8FFCF3" d="M12 4v5.912l5 2.237z" />
        <path fill="#CABCF8" d="m12 4-5 8.148 5-2.235z" />
        <path fill="#CBA7F5" d="M12 15.98V20l5-6.92z" />
        <path fill="#74A0F3" d="M12 20v-4.02l-5-2.9z" />
        <path fill="#CBA7F5" d="m12 15.049 5-2.9-5-2.236z" />
        <path fill="#74A0F3" d="m7 12.149 5 2.9V9.913z" />
        <path
          fill="#202699"
          fillRule="evenodd"
          clipRule="evenodd"
          d="m12 15.048-5-2.9L12 4l5 8.148zm-4.67-3.136 4.588-7.475v5.435zm-.068.204 4.656-2.068v4.768zm4.816-2.068v4.768l4.653-2.7zm0-.176 4.588 2.04-4.588-7.475z"
        />
        <path
          fill="#202699"
          fillRule="evenodd"
          clipRule="evenodd"
          d="m12 15.917-5-2.84L12 20l5-6.924zm-4.44-2.341 4.36 2.48v3.56zm4.519 2.48v3.56l4.36-6.04z"
        />
      </g>
      <defs>
        <clipPath id={clip}>
          <path fill="#fff" d="M0 0h24v24H0z" />
        </clipPath>
      </defs>
    </OfficialIcon>
  );
}

function BnbChainIcon({ size }: { size: number }) {
  const clip = useId();
  return (
    <OfficialIcon size={size}>
      <g clipPath={`url(#${clip})`}>
        <path fill="#F0B90B" d="M24 0H0v24h24z" />
        <path
          fill="#fff"
          d="M7.635 6.449 12 4l4.364 2.449-1.6.907L12 5.809 9.24 7.356zm8.73 3.093-1.6-.906L12 10.182 9.24 8.636l-1.605.906v1.81l2.756 1.546v3.089l1.609.906 1.604-.906v-3.09l2.76-1.546zm0 4.903V12.63l-1.6.907v1.809zm1.141.64-2.76 1.542v1.813l4.365-2.453v-4.898l-1.605.902zm-1.604-7.09 1.6.907v1.81l1.609-.903V7.996l-1.605-.907L15.902 8zm-5.511 9.29v1.808L12 20l1.604-.907V17.29l-1.604.9-1.605-.907zM7.635 14.44l1.6.907v-1.81l-1.6-.906v1.813zm2.756-6.444L12 8.902l1.604-.906L12 7.089 10.395 8zm-3.898.906 1.605-.906-1.6-.907L4.888 8v1.813l1.605.903zm0 3.09-1.604-.903v4.898l4.364 2.453v-1.818L6.498 15.08v-3.093z"
        />
      </g>
      <defs>
        <clipPath id={clip}>
          <path fill="#fff" d="M0 0h24v24H0z" />
        </clipPath>
      </defs>
    </OfficialIcon>
  );
}

function BaseIcon({ size }: { size: number }) {
  const clip = useId();
  return (
    // The upstream @web3icons/react "network" mark (used for the other
    // chains above) draws Base as a rounded-square app-icon glyph, not the
    // brand's actual logomark -- clipped to a circle it leaves a white
    // squircle floating on a blue disc. The real Base logomark (base.org
    // brand kit, github.com/base-org/brand-kit logo/symbol/Base_Symbol_Blue.svg)
    // is authored in its own 111x111 space as a single path: a solid disc
    // with a flat horizontal notch cut through it, open on the left. The
    // path's own fill leaves that notch transparent -- a white square is
    // layered underneath so the notch reads as the brand's white slot
    // regardless of what the icon is rendered against, matching every other
    // sibling mark here (each of which paints its own opaque background
    // square before layering the glyph). It's already an inscribed circle
    // in its own square, so it's passed through as-is (viewBox scaled to
    // match; the source's blue is already the official `#0052FF`) rather
    // than forced into the 24x24 space the other marks use.
    <OfficialIcon size={size} viewBox="0 0 111 111">
      <g clipPath={`url(#${clip})`}>
        <path fill="#fff" d="M0 0h111v111H0z" />
        <path
          fill="#0052FF"
          d="M54.921 110.034C85.359 110.034 110.034 85.402 110.034 55.017C110.034 24.6319 85.359 0 54.921 0C26.0432 0 2.35281 22.1714 0 50.3923H72.8467V59.6416H0C2.35281 87.8625 26.0432 110.034 54.921 110.034Z"
        />
      </g>
      <defs>
        <clipPath id={clip}>
          <path fill="#fff" d="M0 0h111v111H0z" />
        </clipPath>
      </defs>
    </OfficialIcon>
  );
}

function ArbitrumIcon({ size }: { size: number }) {
  const clip = useId();
  return (
    <OfficialIcon size={size}>
      <g clipPath={`url(#${clip})`}>
        <path fill="#213147" d="M24 0H0v24h24z" />
        <path
          fill="#fff"
          d="m13.203 13.216-.787 2.124a.27.27 0 0 0 0 .183l1.354 3.655 1.565-.89-1.879-5.072c-.042-.117-.21-.117-.253 0m1.577-3.573a.135.135 0 0 0-.253 0l-.787 2.124a.27.27 0 0 0 0 .183l2.217 5.985 1.565-.89z"
        />
        <path
          fill="#fff"
          d="M11.999 4.991a.24.24 0 0 1 .111.03l5.969 3.393a.22.22 0 0 1 .112.19v6.787a.22.22 0 0 1-.112.19l-5.969 3.395a.2.2 0 0 1-.111.029.24.24 0 0 1-.113-.03l-5.968-3.39a.22.22 0 0 1-.112-.19v-6.79a.22.22 0 0 1 .112-.19l5.969-3.393a.23.23 0 0 1 .111-.03m0-.991c-.213 0-.426.054-.616.163L5.416 7.556a1.21 1.21 0 0 0-.616 1.05v6.787c0 .433.234.834.616 1.05l5.968 3.394a1.25 1.25 0 0 0 1.232 0l5.968-3.394a1.21 1.21 0 0 0 .616-1.05V8.606a1.21 1.21 0 0 0-.616-1.05l-5.97-3.393A1.24 1.24 0 0 0 11.998 4"
        />
        <path
          fill="#fff"
          d="m8.052 17.943.55-1.482 1.105.905-1.034.93zm3.445-9.823H9.984a.27.27 0 0 0-.254.175l-3.243 8.757 1.565.89L11.623 8.3a.132.132 0 0 0-.127-.179"
        />
        <path
          fill="#fff"
          d="M14.144 8.12h-1.513a.27.27 0 0 0-.253.175l-3.704 10 1.565.89 4.032-10.886a.133.133 0 0 0-.127-.179"
        />
      </g>
      <defs>
        <clipPath id={clip}>
          <path fill="#fff" d="M0 0h24v24H0z" />
        </clipPath>
      </defs>
    </OfficialIcon>
  );
}

function PolygonIcon({ size }: { size: number }) {
  const clip = useId();
  const gradient = useId();
  return (
    <OfficialIcon size={size}>
      <g clipPath={`url(#${clip})`}>
        <path fill={`url(#${gradient})`} d="M24 0H0v24h24z" />
        <path
          fill="#fff"
          d="m15.88 14.86 3.794-2.165a.64.64 0 0 0 .326-.558v-4.33a.64.64 0 0 0-.326-.556L15.88 5.086a.66.66 0 0 0-.65 0L11.432 7.25a.64.64 0 0 0-.325.557v7.737l-2.662 1.517-2.661-1.517v-3.036l2.661-1.517 1.755 1.001V9.958l-1.43-.816a.66.66 0 0 0-.65 0l-3.796 2.165a.64.64 0 0 0-.325.557v4.33c0 .229.124.442.325.557l3.796 2.165c.2.114.45.114.65 0l3.796-2.165a.64.64 0 0 0 .325-.557V8.455l.048-.026 2.613-1.49 2.661 1.516v3.036l-2.661 1.517-1.753-.999v2.037l1.427.814a.66.66 0 0 0 .651 0z"
        />
      </g>
      <defs>
        <linearGradient
          id={gradient}
          x1="3.948"
          x2="19.217"
          y1="16.617"
          y2="7.645"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A726C1" />
          <stop offset=".88" stopColor="#803BDF" />
          <stop offset="1" stopColor="#7B3FE4" />
        </linearGradient>
        <clipPath id={clip}>
          <path fill="#fff" d="M0 0h24v24H0z" />
        </clipPath>
      </defs>
    </OfficialIcon>
  );
}

function SolanaIcon({ size }: { size: number }) {
  const clip = useId();
  const gradient = useId();
  return (
    <OfficialIcon size={size}>
      <g clipPath={`url(#${clip})`}>
        <path fill={`url(#${gradient})`} d="M24 0H0v24h24z" />
        <path
          fill="#fff"
          d="M17.7 8.358a.55.55 0 0 1-.365.145H4.515c-.455 0-.684-.52-.37-.825l2.106-2.03a.54.54 0 0 1 .364-.15h12.87c.46 0 .685.524.365.83zm0 10.005c-.1.09-.23.14-.365.14H4.515c-.455 0-.684-.516-.37-.82l2.106-2.036a.53.53 0 0 1 .364-.145h12.87c.46 0 .685.52.365.825zm0-7.72a.55.55 0 0 0-.365-.14H4.515c-.455 0-.684.515-.37.82l2.106 2.035c.1.091.23.143.364.144h12.87c.46 0 .685-.52.365-.824z"
        />
      </g>
      <defs>
        <linearGradient
          id={gradient}
          x1="4.001"
          x2="20.408"
          y1="50.258"
          y2="50.108"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#599DB0" />
          <stop offset="1" stopColor="#47F8C3" />
        </linearGradient>
        <clipPath id={clip}>
          <path fill="#fff" d="M0 0h24v24H0z" />
        </clipPath>
      </defs>
    </OfficialIcon>
  );
}

export interface ChainIconProps {
  chainId: number;
  /** Display name, used only for the unknown-chain fallback monogram. */
  chainName?: string;
  size?: number;
}

/** Official-logo circular badge for a chainId (see the module-level comment
 * for sourcing/licensing). Falls back to a neutral monogram (from
 * `chainName`'s initial) for chains we don't have a bundled official icon
 * for, e.g. custom/testnet chains such as CROSS (612055). */
export function ChainIcon({ chainId, chainName, size = 20 }: ChainIconProps) {
  switch (chainId) {
    case 1: // Ethereum mainnet
      return <EthereumIcon size={size} />;
    case 56: // BNB Smart Chain
      return <BnbChainIcon size={size} />;
    case 8453: // Base
      return <BaseIcon size={size} />;
    case 42161: // Arbitrum One
      return <ArbitrumIcon size={size} />;
    case 137: // Polygon
      return <PolygonIcon size={size} />;
    case 792703809: // Solana
      return <SolanaIcon size={size} />;
    case 612055: // CROSS -- custom chain, no official public logo to bundle
      return <MonogramBadge size={size} bg="#0b1f3a" text="CR" />;
    default:
      return (
        <MonogramBadge
          size={size}
          bg="#676b7a"
          text={monogram(chainName ?? String(chainId))}
        />
      );
  }
}

export interface TokenIconProps {
  symbol: string;
  /** When provided (non-empty), rendered as an <img> instead of a bundled
   * brand SVG -- lets API-supplied logos take priority. */
  logoUrl?: string;
  size?: number;
}

/** Official-logo circular badge for a token symbol. Prefers `logoUrl`
 * (rendered as an <img>) when supplied; otherwise a bundled official brand
 * logo (see token-logos.ts) for the tokens we ship (ONEUSD, pONEUSD, USDT,
 * USDC), or a neutral symbol-initial monogram for custom tokens (e.g. USDTx)
 * that have no official logo to bundle. */
export function TokenIcon({ symbol, logoUrl, size = 20 }: TokenIconProps) {
  // API-supplied logo wins; otherwise fall back to a bundled brand logo
  // (base64 data URI in token-logos.ts) for the tokens we ship. Rendered as
  // an <img> so each SVG's internal ids are isolated per element (no
  // duplicate-id collisions across trigger + option) and raster logos work.
  const resolvedLogo =
    logoUrl && logoUrl.trim().length > 0 ? logoUrl.trim() : bundledTokenLogo(symbol);
  if (resolvedLogo) {
    return (
      <img
        src={resolvedLogo}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        style={{ display: "block", flex: "none", borderRadius: "50%", objectFit: "cover" }}
      />
    );
  }

  return <MonogramBadge size={size} bg="#676b7a" text={monogram(symbol)} />;
}
